import type {
  ExperienceCategory,
  ExperienceListDto,
  ExperienceStatus,
  ExperienceTestType,
  PluginConfigVersion,
} from "@intelligems/ig-types";

export const shortId = (value: string): string => {
  if (value.length > 12) {
    return value.split("-").pop() ?? "";
  }
  return value;
};
export const vanillaChunk = <T>(array: T[], size: number): T[][] => {
  const chunked_arr: T[][] = [];
  let index = 0;
  while (index < array.length) {
    chunked_arr.push(array.slice(index, size + index));
    index += size;
  }
  return chunked_arr;
};

export const sleep = async (time: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, time);
  });
};

export const overwriteMerge = (
  _destinationArray: any,
  _options: any,
  sourceArray: any
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
) => sourceArray;

export const hexToRgbA = (hexCode: string, opacity = 1) => {
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
};

export const hexToHex8 = (hexCode: string, opacity = 1) => {
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  // If already 8-digit hex, return as-is
  if (hex.length === 8) {
    return `#${hex}`;
  }

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  // Convert opacity (0-1) to hex (00-FF)
  const alpha = Math.round(opacity * 255);
  const alphaHex = alpha.toString(16).padStart(2, "0");

  return `#${hex}${alphaHex}`;
};

/**
 * Sorts in ascending order
 */
export function sortBy<T extends { [Key in K]: number }, K extends keyof T>(
  items: T[],
  key: K
) {
  [...items].sort((a, b) => a[key] - b[key]);
  return items;
}

// Usages of this fn are expecting null response when input is null/undefined
// We should consider updating the type signature to represent usage
export const ensureNumber = (
  input: string | number | null | undefined
): number => {
  if (typeof input === "string") {
    return parseFloat(input);
  }
  return <number>input;
};

export const ensurePercent = (input: number): number => {
  if (input > 1) {
    return input / 100;
  }
  return input;
};

export const ensureString = (
  input: string | number | null | undefined
): string => {
  if (typeof input === "number") {
    return input.toString();
  }
  return <string>input;
};

export function ensureDecimalString(
  input: string | number | null | undefined,
  precision: number = 2
) {
  if (typeof input === "number") {
    return round(input, precision).toFixed(precision);
  } else if (typeof input === "string") {
    const parsed = parseFloat(input);
    if (isNaN(parsed)) {
      return input;
    }
    return round(parsed, precision).toFixed(precision);
  } else {
    return input;
  }
}

export const dotSlice = (input: string, maxLength: number): string => {
  if (input.length <= maxLength - 3) return input;
  return input.slice(0, maxLength - 3) + "...";
};

function round(value: number, precision: number = 2) {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

export const getExperimentType = (
  experience: ExperienceListDto
): ExperienceTestType => {
  if (experience.type === "checkoutBlock") {
    return "checkout";
  } else if (experience.type === "postPurchase") {
    return "post_purchase";
  } else if (
    !(experience.type === "shipping") &&
    !(experience.type === "pricing") &&
    !(experience.type === "personalization/offer")
  ) {
    return "content";
  } else if (experience.type === "shipping") {
    return "shipping";
  } else if (experience.type === "pricing") {
    return "pricing";
  } else if (experience.type === "personalization/offer") {
    return "offer";
  } else {
    return "content";
  }
};

const TEST_TYPE_LABELS: Record<ExperienceTestType, string> = {
  pricing: "Pricing",
  shipping: "Shipping",
  offer: "Offer",
  content: "Content",
  checkout: "Checkout",
  post_purchase: "Post-purchase",
};

export const getExperienceTestTypeLabel = (
  testType: ExperienceTestType
): string => TEST_TYPE_LABELS[testType];

export const ensurePriceNumber = (
  input: string | number,
  priceRegex: RegExp
): number => {
  if (typeof input === "string") {
    if (priceRegex.test(input)) {
      return ensureNumber(input.match(priceRegex)![0]);
    }
    return ensureNumber(input);
  } else {
    return input;
  }
};

export const getTextColor = (hexOrRgb: string) => {
  let color: string;
  if (hexOrRgb.includes("#")) {
    color = hexToRgbA(hexOrRgb);
  } else {
    color = hexOrRgb;
  }
  const rgba = color.match(/\d+/g);
  if (rgba) {
    if (+rgba[0] * 0.299 + +rgba[1] * 0.587 + +rgba[2] * 0.114 > 186) {
      return "#000000";
    } else {
      return "#FFFFFF";
    }
  }
  return "#000000";
};

export const snakeToPascalCase = (str: string) => {
  return str
    .split("/")
    .map((snake) =>
      snake
        .split("_")
        .map((substr) => substr.charAt(0).toUpperCase() + substr.slice(1))
        .join(" ")
    )
    .join("/");
};

export const camelToSnakeCase = (str: string) =>
  str
    .replace(/([a-z])([A-Z])/g, (_letter, first: string, second: string) => {
      return `${first}_${second.toLowerCase()}`;
    })
    // 2nd replace is to handle inconsistent cases like updateVariantsInATCForm & interceptAtcXhr
    .replace(/([A-Z])([a-z]?)/g, (_letter, first: string, second: string) => {
      if (first && !second) {
        return first.toLowerCase();
      }
      return `_${first.toLowerCase()}${second}`;
    });

export const sentenceToSnakeCase = (input: string): string => {
  return input.trim().toLowerCase().replace(/\s+/g, "_");
};

export const camelToSentenceCase = (str: string) => {
  // Insert a space before any uppercase letter
  const resultWithSpaces = str.replace(/([A-Z])/g, " $1");

  // Capitalize the first letter and make the rest lowercase
  const finalResult =
    resultWithSpaces.charAt(0).toUpperCase() +
    resultWithSpaces.slice(1).toLowerCase();

  return finalResult;
};

export const snakeToCamelCase = (str: string) =>
  str.toLowerCase().replace(/_([a-z])/g, (_match, letter: string) => {
    return letter.toUpperCase();
  });

export const camelToPascalCase = (str: string) => {
  const spaced = str
    .replace(
      /([^A-Z])([A-Z])/g,
      (_letter, nonUppercase: string, uppercase: string) =>
        `${nonUppercase} ${uppercase.toUpperCase()}`
    )
    // 2nd replace is to handle inconsistent cases like updateVariantsInATCForm & interceptAtcXhr
    .replace(/([A-Z])([a-z]?)/g, (_letter, first: string, second: string) => {
      if (first && !second) {
        return first;
      }
      return ` ${first}${second}`;
    });
  return capitalizeFirstLetter(spaced);
};

export function capitalizeFirstLetter(str?: string) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

export function formatTimezoneString(timezoneString: string) {
  // Split the string on the "/" character
  const parts = timezoneString.split("/");

  // Capitalize the first letter of each part
  const formattedParts = parts.map(
    (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  );

  // Join the parts back together with a space
  return formattedParts.join(" ");
}
export function uppercaseSnakeToNormal(str: string) {
  // Split the string by underscores
  const words = str.split("_");

  // Capitalize the first letter of each word and join them with a space
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
export function capitalizeFirstLetterEachWord(sentence) {
  return sentence
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
export function hasStringDuplicates(array: string[]): boolean {
  return new Set(array.map((arr) => arr.toLowerCase())).size !== array.length;
}

export const invertObject = <
  A extends string | number | symbol,
  B extends string | number | symbol,
>(
  obj: Record<A, B>
): Record<B, A> => {
  // @ts-ignore
  const invertedObject: Record<B, A> = {};
  for (const key in obj) {
    invertedObject[obj[key]] = key;
  }
  return invertedObject;
};

export const dotToDash = (str: string) => str.replace(/\./g, "-");
export const dotToUnderscore = (str: string) => str.replace(/\./g, "_");
export const dashToDot = (str: string) => str.replace(/-/g, ".");

export const stringToBoolean = (value: string): boolean => {
  value = value.trim().toLowerCase();
  if (["y", "yes", "t", "true", "on", "1"].includes(value)) return true;
  if (["n", "no", "f", "false", "off", "0"].includes(value)) return false;
  throw Error(`Invalid truth value ${value}`);
};

export const HeadlessToPluginConfigMap: Record<string, PluginConfigVersion> = {
  "1.0.0": "2024-05-05",
  "1.0.1": "2024-05-05",
  "1.0.2": "2024-05-05",
  "1.0.3": "2024-05-05",
  "1.0.4": "2024-05-05",
  "1.1.0": "2024-05-05",
  "1.1.1": "2024-05-05",
  "1.1.2": "2024-05-05",
  "1.1.3": "2024-05-05",
  "1.1.4": "2024-05-05",
  "1.2.x": "2024-05-05",
};

export const removeTrailingSlash = (url: string) => {
  return url.endsWith("/") && url.length > 1 ? url.slice(0, -1) : url;
};

export function sendBeacon(endpoint: string, body: { Detail: object }) {
  if (navigator.sendBeacon) {
    return navigator.sendBeacon(endpoint, JSON.stringify(body));
  }
}

export const officialCategoryName = (
  category: ExperienceCategory,
  shouldCapitalizeFirstLetter: boolean
) => {
  let officialName =
    category === "personalization" ? "personalization" : "test";

  if (shouldCapitalizeFirstLetter) {
    officialName = capitalizeFirstLetter(officialName);
  }

  return officialName;
};

export const officialStatusName = (status: ExperienceStatus) => {
  return `${status === "started" ? "Active" : capitalizeFirstLetter(status)}`;
};

export function generateUUIDv4(): string {
  const randomNumbers = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return (
    randomNumbers() +
    randomNumbers() +
    "-" +
    randomNumbers() +
    "-" +
    "4" +
    randomNumbers().substr(0, 3) +
    "-" +
    ((Math.random() * 4) | (0 + 8)).toString(16) +
    randomNumbers().substr(1, 3) +
    "-" +
    randomNumbers() +
    randomNumbers() +
    randomNumbers()
  );
}

type Procedure = (...args: any[]) => void;

export function leadingDebounce<T extends Procedure>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let isLeading = true;

  const debouncedFunction = (...args: Parameters<T>): void => {
    if (isLeading) {
      func(...args); // Call the function on the leading edge
      isLeading = false; // Mark that the leading call has happened
    }

    if (timeout) {
      clearTimeout(timeout); // Clear the previous timeout if it exists
    }

    timeout = setTimeout(() => {
      isLeading = true; // Reset for the next interval
      timeout = null; // Cleanup timeout
    }, wait);
  };

  return debouncedFunction as T;
}

// removes trailing /, www., and ensures https://
export const formatDomain = (str: string) => {
  let domain = str.endsWith("/") ? str.substring(0, str.length - 1) : str;
  domain = domain.replace("www.", "");
  if (!domain.includes("https://") && domain.includes("http://")) {
    domain = domain.replace("http://", "https://");
  }
  return domain;
};

export const cleanUrlPath = (str: string): string => {
  return str
    .replace(/([^:])(\/\/+)/g, "$1/")
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
};

export const getRandomIntInclusive = (min: number, max: number) => {
  min = Math.ceil(min); // Ensure min is an integer
  max = Math.floor(max); // Ensure max is an integer
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export function jsonToCsv(jsonData: object[]): string {
  if (jsonData.length === 0) {
    return "";
  }

  const header = Object.keys(jsonData[0]).join(",") + "\n";

  const rows = jsonData.map((obj) =>
    Object.values(obj)
      .map((value: any) => {
        if (typeof value === "string") {
          // If the value contains a comma, enclose it in double quotes
          if (value.includes(",")) {
            return `"${value}"`;
          }
          return value;
        }
        // Convert non-string values to strings
        return String(value);
      })
      .join(",")
  );

  return header + rows.join("\n");
}

export const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

export const safeParseJson = <T>(input: unknown): T => {
  let parsedInput = input;
  if (typeof input !== "string") {
    return parsedInput as T;
  }
  try {
    parsedInput = JSON.parse(input) as T;
  } catch (_e) {
    // do nothing
  }
  return parsedInput as T;
};
